import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { BillService } from "@/services/BillService"
import { IBill } from "@/models/bill/bill.types"
import { IBillBreakdownLine } from "@/models/billBreakdown/billBreakdown.types"
import { IBillEmail } from "@/models/billEmail/billEmail.types"

/**
 * The three queries behind a bill resolve independently, so one can fail while
 * the others succeed. `QueryResult<T>.data` is typed as always-present, which
 * is not true of a query that errored — so the fields that are known to fail on
 * their own are declared optional here. That keeps consumers honest: dropping
 * the guard in BillModal now fails type-check rather than blanking the modal at
 * runtime (SoErrorBoundary swallows the render error, so there is no clue).
 *
 * `billEmail` fails routinely on staging/UAT: Junifer returns a
 * BillingErrorResponse for it on most bills.
 */
export interface IBillAggregate {
  billInfo: IBill
  billBreakdownLines: IBillBreakdownLine[]
  billEmail?: IBillEmail
}

export const getBill = (billId: number, billingAccountId: number | null) => {
  const queries = new QueryAggregator()

  const billQuery = BillService.getBill(billId, billingAccountId)
  const billBreakdownQuery = BillService.getBillBreakdown(billId)
  const billEmailQuery = BillService.getBillEmail(billId)

  queries.add(billQuery)
  queries.add(billBreakdownQuery)
  queries.add(billEmailQuery)

  const bill: IBillAggregate = {
    billInfo: billQuery.data,
    billBreakdownLines: billBreakdownQuery.data,
    billEmail: billEmailQuery.data,
  }

  return queries.setData(bill).getAggregate()
}
